import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TickerBar } from '@/components/landing/TickerBar';
import { BrandMark } from '@/components/layout/BrandMark';

/**
 * Editorial auth layout. Two columns:
 *   - LEFT (5/12): the form column. White card, hairline border,
 *     emerald top-edge stripe, single-emphasis on one word in the
 *     headline. No marketing claims here — just the form.
 *   - RIGHT (7/12): editorial preview. Live-feeling ticker bar +
 *     a fixed printed-report mock that shows what the user will see
 *     once signed in. Slate background.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  rightSlot,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background grid-paper-faint">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        {/* Left — the form */}
        <div className="lg:col-span-5 flex flex-col">
          <div className="px-6 sm:px-10 lg:px-14 py-7">
            <BrandMark />
          </div>

          <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-14 pb-14">
            <div className="w-full max-w-md">
              <h1 className="display text-3xl md:text-[2.4rem] font-medium tracking-tight leading-tight text-balance">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-3 text-[14.5px] text-muted-foreground leading-relaxed text-pretty">{subtitle}</p>
              )}
              <div className="mt-8">{children}</div>
            </div>
          </div>

          <div className="px-6 sm:px-10 lg:px-14 py-6 border-t border-border text-[12px] text-muted-foreground flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/" className="hover:text-foreground transition-colors">{t('nav.product') ?? 'Product'}</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">{t('nav.pricing')}</Link>
            <Link to="/compare" className="hover:text-foreground transition-colors">{t('nav.compare')}</Link>
            <span className="ml-auto">© {new Date().getFullYear()} CadencEZ</span>
          </div>
        </div>

        {/* Right — preview pane */}
        <aside className="hidden lg:flex lg:col-span-7 bg-ink-50/60 border-l border-border flex-col">
          <TickerBar />
          <div className="flex-1 flex items-center justify-center p-10 xl:p-16">
            <div className="w-full max-w-2xl">
              {rightSlot ?? <DefaultPreviewPane />}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function DefaultPreviewPane() {
  const { t } = useTranslation();
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-editorial">
      {/* Faux report header */}
      <div className="flex items-baseline justify-between border-b border-border px-5 py-3 bg-ink-50/70">
        <span className="display text-[15px] font-semibold">{t('hero.mock_variance_title')}</span>
        <span className="num text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('hero.mock_period')}
        </span>
      </div>

      <table className="w-full text-[12.5px]">
        <thead className="bg-ink-50/40">
          <tr className="border-b border-border">
            <th className="text-left px-5 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('variance.th_account')}
            </th>
            <th className="text-right px-3 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('variance.th_budget')}
            </th>
            <th className="text-right px-3 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('variance.th_actual')}
            </th>
            <th className="text-right px-5 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Δ</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {[
            ['4100', 'EU OPEX',    '¥39.5M',  '¥48.2M',  '+22.0%', 'text-rose-600'],
            ['5200', 'APAC sales', '¥128.0M', '¥146.5M', '+14.4%', 'text-emerald-700'],
            ['7100', 'Headcount',  '¥92.0M',  '¥99.1M',  '+7.7%',  'text-rose-600'],
            ['8100', 'Marketing',  '¥22.0M',  '¥14.8M',  '−32.7%', 'text-emerald-700'],
          ].map((r) => (
            <tr key={r[0]}>
              <td className="px-5 py-2.5">
                <span className="num text-muted-foreground text-[10.5px]">{r[0]}</span>
                <span className="ml-2 text-foreground/95">{r[1]}</span>
              </td>
              <td className="num text-right px-3 py-2.5 text-foreground/75">{r[2]}</td>
              <td className="num text-right px-3 py-2.5 text-foreground">{r[3]}</td>
              <td className={`num text-right px-5 py-2.5 font-semibold tabular-col ${r[5]}`}>{r[4]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-border px-5 py-4 bg-ink-50/30">
        <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 mb-1">✦ {t('showcase.ai_summary')}</p>
        <p className="text-[12px] leading-relaxed text-foreground/85">
          EU OPEX overshoot driven by Berlin office expansion. A one-off vendor renovation accounts for ¥4.1M of the ¥8.7M total. Recurring driver: headcount.
        </p>
      </div>
    </div>
  );
}
